import { Box, Stack } from '@mui/material';
import { CircleCheck, Clock } from 'lucide-react';
import Typography from '../../../../../../components/Typography/Typography';
import LangEnum from '../../../../../../lang/LangEnum';
import { useTranslation } from '../../../../../translation/TranslationContext';
import RachelMarkdown from '../../../RachelMarkdown/RachelMarkdown';
import { RachelReasoningStep } from '../../RachelReasoningTrace';

interface RachelReasoningTimelineProps {
  steps: RachelReasoningStep[];
  isDone: boolean;
}

const RAIL_WIDTH = 20;
const ICON_SIZE = 16;

const RachelReasoningTimeline = ({ steps, isDone }: RachelReasoningTimelineProps) => {
  const { translate } = useTranslation();

  return (
    <Box>
      {steps.map((step, index) => {
        const hasNodeBelow = index < steps.length - 1 || isDone;

        return (
          <Stack key={step.id} direction="row" gap={1.5}>
            <Stack alignItems="center" sx={{ width: RAIL_WIDTH, flexShrink: 0 }}>
              <Box sx={{ display: 'flex', color: 'text.secondary', mt: '2px' }}>
                <Clock size={ICON_SIZE} />
              </Box>
              {hasNodeBelow && (
                <Box sx={{ flex: 1, width: '2px', bgcolor: 'divider', borderRadius: 1, mt: 0.5 }} />
              )}
            </Stack>

            <Box sx={{ flex: 1, minWidth: 0, pb: 2 }}>
              <RachelMarkdown variant="body2" colorToken="text.secondary">
                {step.text}
              </RachelMarkdown>
              {step.sub && (
                <Typography
                  variant="body3-regular"
                  component="div"
                  color="text.disabled"
                  marginTop={1}
                >
                  {step.sub}
                </Typography>
              )}
            </Box>
          </Stack>
        );
      })}

      {isDone && (
        <Stack direction="row" gap={1.5} alignItems="center">
          <Stack alignItems="center" sx={{ width: RAIL_WIDTH, flexShrink: 0 }}>
            <Box sx={{ display: 'flex', color: 'text.secondary' }}>
              <CircleCheck size={18} />
            </Box>
          </Stack>
          <Typography
            variant="body2-regular"
            sx={(theme) => ({
              color: theme.palette.text.secondary,
              fontWeight: theme.typography.fontWeightMedium,
              lineHeight: 1.65,
            })}
          >
            {translate(LangEnum.RACHEL_REASONING_DONE)}
          </Typography>
        </Stack>
      )}
    </Box>
  );
};

export default RachelReasoningTimeline;
